import React, { Suspense, lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import Layout from "../layout/MainLayout";
import Homepage from "../pages/homepage";
import NotFound from "../pages/not-found";

/*
 * Route-level code splitting.
 *
 * The whole site previously shipped as one 1,023 KB JavaScript chunk, so a visitor
 * landing on a single chain page downloaded, parsed and executed every page on the
 * site before anything rendered. The homepage and the 404 stay in the main bundle
 * (one is the most common entry, the other must render instantly); everything else
 * loads on demand.
 */
const AboutUs = lazy(() => import("../pages/about-us"));
const Services = lazy(() => import("../pages/services"));
const AuditedReports = lazy(() => import("../pages/Reports"));
const ReportDetail = lazy(() => import("../pages/Reports/ReportDetail"));
const FAQPage = lazy(() => import("../pages/faq"));
const ZKSecurityPage = lazy(() => import("../pages/zk-security"));
const Contact = lazy(() => import("../pages/contact"));
const PrivacyPolicy = lazy(() => import("../pages/privacy-policy"));
const TermsOfUse = lazy(() => import("../pages/terms-of-use"));
const DmForAudit = lazy(() => import("../pages/dm-for-audit"));
const Team = lazy(() => import("../pages/Team"));
const AuditCost = lazy(() => import("../pages/audit-cost"));
const AuditProcess = lazy(() => import("../pages/audit-process"));
const Tools = lazy(() => import("../pages/tools"));
const BastionSecurity = lazy(() => import("../pages/bastion-security"));
const AIMCPAudit = lazy(() => import("../pages/ai-mcp-audit/AIMCPAudit"));
const SwayAudit = lazy(() => import("../pages/sway-audit"));
const LendingAudit = lazy(() => import("../pages/lending-audit"));
const EthereumSmartContractAudit = lazy(() => import("../pages/ethereum-smart-contract-audit"));
const ChainAudit = lazy(() => import("../pages/chains/ChainAudit"));
const ServiceAudit = lazy(() => import("../pages/services/ServiceAudit"));
const LanguageAudit = lazy(() => import("../pages/languages/LanguageAudit"));
const IndustriesHub = lazy(() => import("../pages/industries"));
const IndustryPage = lazy(() => import("../pages/industries/IndustryPage"));
const LocationsHub = lazy(() => import("../pages/locations"));
const LocationPage = lazy(() => import("../pages/locations/LocationPage"));

/* Minimal fallback: no spinner overlay, because a full-screen loader blocks LCP and
 * makes the route look empty to anything measuring paint. */
const Fallback = () => <div className="min-h-screen bg-black" aria-busy="true" />;

const page = (Component) => (
    <Suspense fallback={<Fallback />}>
        <Component />
    </Suspense>
);

export const router = createBrowserRouter([
    {
        path: "/",
        element: <Layout />,
        children: [
            { path: "/",                    element: <Homepage /> },
            { path: "/about-us",            element: page(AboutUs) },
            { path: "/team",                element: page(Team) },
            { path: "/services",            element: page(Services) },
            { path: "/reports",             element: page(AuditedReports) },
            // One crawlable HTML page per published audit — same play as the public
            // audit libraries of Cyfrin/QuillAudits. Data: src/data/auditReports.js.
            { path: "/reports/:slug",       element: page(ReportDetail) },
            { path: "/faq",                 element: page(FAQPage) },
            { path: "/zk-security",         element: page(ZKSecurityPage) },
            { path: "/contact",             element: page(Contact) },
            { path: "/privacy-policy",      element: page(PrivacyPolicy) },
            { path: "/terms-of-use",        element: page(TermsOfUse) },
            // /forensic intentionally unrouted: .htaccess 301s it to
            // /services/blockchain-forensics, so it must not render or be listed in the sitemap.

            // Commercial and informational pillars
            { path: "/smart-contract-audit-cost", element: page(AuditCost) },
            { path: "/audit-process",            element: page(AuditProcess) },
            { path: "/tools",                    element: page(Tools) },
            { path: "/products/bastion-security", element: page(BastionSecurity) },

            // Search-only landing page: intentionally not linked from any menu
            { path: "/dm-for-audit",        element: page(DmForAudit) },

            // Canonical service routes
            { path: "/services/lending-audit",                          element: page(LendingAudit) },
            { path: "/services/ai-mcp-audit",                           element: page(AIMCPAudit) },
            { path: "/services/chain/ethereum-smart-contract-audit",    element: page(EthereumSmartContractAudit) },
            { path: "/services/lang/sway-audit",                        element: page(SwayAudit) },
            { path: "/services/chain/:slug",                            element: page(ChainAudit) },
            { path: "/services/lang/:slug",                             element: page(LanguageAudit) },
            { path: "/services/:slug",                                  element: page(ServiceAudit) },

            // Buyer-segment and market landing pages
            { path: "/industries",          element: page(IndustriesHub) },
            { path: "/industries/:slug",    element: page(IndustryPage) },
            { path: "/locations",           element: page(LocationsHub) },
            { path: "/locations/:slug",     element: page(LocationPage) },

            // 404 — never redirect; show proper not-found page
            { path: "*", element: <NotFound /> },
        ]
    }
]);
